import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import { Mail, Save, RotateCcw, Eye, Send, Loader2, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TemplateEditor = ({ t, variables, onSaved }) => {
  const [form, setForm] = useState(t);
  const [busy, setBusy] = useState('');
  const [preview, setPreview] = useState(null);
  const [testTo, setTestTo] = useState('');
  useEffect(() => setForm(t), [t]);
  const dirty = JSON.stringify(form) !== JSON.stringify(t);
  const body = { subject: form.subject, message: form.message, cta_label: form.cta_label, cta_path: form.cta_path, enabled: form.enabled };

  const save = async () => {
    setBusy('save');
    try { await axios.put(`${API}/admin/email-templates/${t.key}`, body, { withCredentials: true }); toast.success(`${t.name} saved — live immediately`); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || 'Save failed'); } finally { setBusy(''); }
  };
  const reset = async () => {
    if (!window.confirm('Reset this template to the default wording?')) return;
    setBusy('reset');
    try { await axios.delete(`${API}/admin/email-templates/${t.key}`, { withCredentials: true }); toast.success('Reset to default'); onSaved(); }
    catch { toast.error('Reset failed'); } finally { setBusy(''); }
  };
  const doPreview = async () => {
    setBusy('preview');
    try { const r = await axios.post(`${API}/admin/email-templates/preview`, body, { withCredentials: true }); setPreview(r.data); }
    catch { toast.error('Preview failed'); } finally { setBusy(''); }
  };
  const testSend = async () => {
    if (!testTo.includes('@')) { toast.error('Enter an email'); return; }
    setBusy('test');
    try {
      const r = await axios.post(`${API}/admin/email-templates/test-send`, { ...body, to: testTo }, { withCredentials: true });
      if (r.data.sent) toast.success(`Test sent to ${testTo}`); else toast.error('Test not delivered', { description: r.data.error, duration: 10000 });
    } catch { toast.error('Test failed'); } finally { setBusy(''); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-5" data-testid={`template-${t.key}`}>
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div>
          <div className="font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>{t.name} {t.customized && <span className="px-1.5 py-0.5 rounded bg-[#002FA7]/10 text-[#002FA7] text-[10px] font-bold uppercase tracking-widest">customized</span>}</div>
          <div className="text-xs text-slate-500">{t.when}</div>
        </div>
        <label className="ml-auto flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} className="h-4 w-4 accent-[#002FA7]" data-testid={`template-enabled-${t.key}`} /> Enabled
        </label>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <label className="text-xs font-semibold text-slate-600 md:col-span-2">Subject
          <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid={`template-subject-${t.key}`} className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-normal text-slate-900" />
        </label>
        <label className="text-xs font-semibold text-slate-600 md:col-span-2">Message
          <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4} data-testid={`template-message-${t.key}`} className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-normal text-slate-900" />
        </label>
        <label className="text-xs font-semibold text-slate-600">Button label
          <input value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} data-testid={`template-cta-${t.key}`} className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-normal text-slate-900" />
        </label>
        <label className="text-xs font-semibold text-slate-600">Button link (path)
          <input value={form.cta_path} onChange={(e) => setForm({ ...form, cta_path: e.target.value })} data-testid={`template-path-${t.key}`} className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-normal text-slate-900" />
        </label>
      </div>
      <div className="text-[11px] text-slate-500 mt-2">Variables: {variables.map((v) => <code key={v} className="mx-1 px-1 bg-slate-100 rounded">{v}</code>)}</div>
      <div className="flex flex-wrap items-center gap-2 mt-3">
        <button onClick={save} disabled={!dirty || busy === 'save'} data-testid={`template-save-${t.key}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-[#002FA7] hover:bg-[#00247D] text-white text-xs font-semibold disabled:opacity-40">{busy === 'save' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save</button>
        <button onClick={doPreview} disabled={busy === 'preview'} data-testid={`template-preview-${t.key}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 text-xs font-semibold"><Eye className="w-3.5 h-3.5" /> Preview</button>
        {t.customized && <button onClick={reset} disabled={busy === 'reset'} data-testid={`template-reset-${t.key}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 text-xs font-semibold"><RotateCcw className="w-3.5 h-3.5" /> Reset to default</button>}
        <div className="ml-auto flex items-center gap-1">
          <input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="Send a test to…" data-testid={`template-test-to-${t.key}`} className="border border-slate-300 rounded-md px-2 py-1.5 text-xs w-48" />
          <button onClick={testSend} disabled={busy === 'test'} data-testid={`template-test-send-${t.key}`} className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md border border-slate-300 text-slate-700 text-xs font-semibold"><Send className="w-3.5 h-3.5" /> Test</button>
        </div>
      </div>
      {preview && (
        <div className="mt-4 border border-slate-200 rounded-md overflow-hidden" data-testid={`template-preview-frame-${t.key}`}>
          <div className="px-3 py-2 bg-slate-50 text-xs text-slate-600 border-b border-slate-200 flex items-center justify-between"><span>Subject: <b>{preview.subject}</b></span><button onClick={() => setPreview(null)} className="text-slate-400 hover:text-slate-700">close</button></div>
          <iframe title="preview" srcDoc={preview.html} className="w-full h-[420px] bg-white" />
        </div>
      )}
    </div>
  );
};

const SuperAdminEmailTemplates = () => {
  const [data, setData] = useState(null);
  const load = () => axios.get(`${API}/admin/email-templates`, { withCredentials: true }).then((r) => setData(r.data)).catch(() => toast.error('Could not load templates'));
  useEffect(() => { load(); }, []);
  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-5xl mx-auto" data-testid="email-templates-page" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
        <header className="mb-6">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 flex items-center gap-3" style={{ fontFamily: 'Work Sans, sans-serif' }}><Mail className="w-8 h-8 text-[#002FA7]" /> Email Templates</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-3xl">Edit the welcome sequence, trial reminders and win-back offer. Changes apply to the next send — no redeploy needed. <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> Every send is logged in the Delivery Log.</span></p>
        </header>
        {!data ? <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div> : (
          <div className="space-y-4">{data.templates.map((t) => <TemplateEditor key={t.key} t={t} variables={data.variables} onSaved={load} />)}</div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SuperAdminEmailTemplates;
